import React, { FC, useEffect } from 'react';
import {
    IResourceComponentsProps,
    useNavigation,
    useTranslate,
} from '@refinedev/core';
import { Form, Col, Row, FormProps } from 'antd';
import { SaveButtonProps, useForm, useSelect } from '@refinedev/antd';
import {
    IAtcCode,
    ICategory,
    ICountryCertificate,
    IApplication,
} from '../../../interfaces/application.interface';
import { TranslatedBreadcrumbs } from '../../../components/translated-breadcrumbs';
import hydrateVErrors from '../../../utils/validate';
import { CertificatesInCountries } from '../../../components/applications/create/certificates-in-countries';
import { Compositions } from '../../../components/applications/create/compositions';
import { Applications } from '../../../components/applications/create/applications';
import { TaggedInput } from '../../../components/tagged-input';
import { Packages } from 'components/applications/create/packages';
import {
    formatDate,
    removeObject,
    splitStr,
    storeObject,
} from '../../../utils/accessory';
import isJsonString from '../../../utils/json-check';
import { SaveOutlined } from '@ant-design/icons';
import { InternalLayout } from '../../../layouts/InternalLayout';
import styles from './create.module.scss';
import { Button, Input, FormItem, Select, Checkbox } from 'components/ui';

const STORE_KEY: string = 'create';

export const ApplicationCreate: FC<IResourceComponentsProps> = () => {
    const t = useTranslate();
    const { replace } = useNavigation();

    // @ts-ignore
    const { formProps, saveButtonProps } = useForm<any>({
        onMutationSuccess: ({ data }, variables, context) => {
            let payload = data?.data;
            let appId: string = payload?.id;
            appId && onAfterAdd(appId);
        },
        onMutationError: error => {
            storeObject(STORE_KEY, error?.config?.data);

            const validationErrors = hydrateVErrors(error);

            // eslint-disable-next-line array-callback-return
            let errFields = Object.keys(validationErrors).map((key: string) => {
                let error = validationErrors?.[key];
                let messages = errorManipulation(error);

                return {
                    name: key
                        .split('.')
                        .map(i => (Number.isInteger(+i) ? +i : i)),
                    errors: [messages],
                };
            });

            //@ts-ignore
            formProps?.form?.setFields(errFields);
        },
        redirect: false,
        successNotification: {
            message: t(
                'notifications.the_upload_is_complete_and_wait_for_it_to_be_processed_successfully_check_the_status_of_dossier_processing_after_some_time'
            ),
            type: 'success',
        },
    });

    const form = formProps?.form;

    const onAfterAdd = (appId: string): void => {
        if (appId) {
            removeObject(STORE_KEY);
            form?.resetFields();

            setTimeout(
                () => (window.location.href = `/applications/edit/${appId}`),
                1000
            );
        }
    };

    const errorManipulation = (error: any[]) => {
        return error?.map((i: string) => {
            let [article, message, ...rest] = i.split(' ');
            let text = rest.join(' ');
            return t(`${article} ${message.split('.').at(-1)} ${text}`);
        });
    };

    const outputDate = (item: string) => {
        let splited: string = splitStr(item, 'T')?.[0];

        return formatDate(splited);
    };

    useEffect(() => {
        const setFormFields = () => {
            try {
                // @ts-ignore
                if (!form?._init) {
                    return;
                }

                let formValues: any = localStorage.getItem(STORE_KEY) ?? {};
                let parsedValues = isJsonString(formValues)
                    ? JSON.parse(formValues)
                    : formValues;
                let objKeys = Object?.keys(parsedValues);

                for (let key in objKeys) {
                    let name = objKeys[key];
                    let item = parsedValues[name];

                    let value;
                    switch (name) {
                        case 'file_id':
                            value = item;
                            break;
                        case 'certificates_in_countries':
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            value = item?.map(
                                (country: ICountryCertificate) => {
                                    let date = outputDate(
                                        country?.certificateDate
                                    );
                                    return {
                                        certificateDate: date,
                                        certificateNum: country?.certificateNum,
                                        countryId: country?.countryId,
                                    };
                                }
                            );

                            break;
                        case 'applications':
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            value = item?.map((application: IApplication) => {
                                let date = outputDate(
                                    application?.mohApplicationDate
                                );

                                return {
                                    mohApplicationNumber:
                                        application?.mohApplicationNumber,
                                    typeId: application?.typeId,
                                    mohApplicationDate: date,
                                    procedureNotes: application?.procedureNotes,
                                };
                            });
                            break;
                        default:
                            value = item;
                    }

                    if (value) {
                        form.setFieldsValue({
                            [name]: value,
                        });
                    }
                }
            } catch (e) {
                console.error(e);
            }
        };

        setFormFields();
    }, [form]);

    const { selectProps: categoryProps } = useSelect<ICategory>({
        resource: 'dictionary/application-types',
        defaultValue: [],
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        meta: {},

        pagination: {
            mode: 'server',
        },
    });

    const { selectProps: pharmaceuticalProps } = useSelect<ICategory>({
        resource: 'dictionary/pharmaceutical-forms',
        defaultValue: [],
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        meta: {},

        pagination: {
            mode: 'server',
        },
    });

    const { selectProps: manufacturersProps } = useSelect<ICategory>({
        resource: 'dictionary/manufacturers',
        defaultValue: [],
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        meta: {},

        pagination: {
            mode: 'server',
        },
    });

    const { selectProps: applicantProps } = useSelect<ICategory>({
        resource: 'dictionary/applicants',
        defaultValue: [],
        optionLabel: 'name',
        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        meta: {},

        pagination: {
            mode: 'server',
        },
    });

    const { selectProps: representativeProps } = useSelect<ICategory>({
        resource: 'dictionary/representative',
        defaultValue: [],
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        meta: {},

        pagination: {
            mode: 'server',
        },
    });

    const { selectProps: atcCodeProps } = useSelect<IAtcCode>({
        defaultValue: [],
        meta: {},
        resource: 'dictionary/atc-classifiers',
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        queryOptions: {
            select: ({ data, total }) => {
                let processedData = data.map((item: IAtcCode) => {
                    return {
                        id: item.id,
                        name: `${item.code} - ${item.name}`,
                    };
                });

                return {
                    data: processedData,
                    total: total,
                };
            },
        },

        pagination: {
            mode: 'server',
        },
    });

    const { selectProps: additionalAtcCodeProps } = useSelect<IAtcCode>({
        defaultValue: [],
        meta: {},
        resource: 'dictionary/atc-classifiers',
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        queryOptions: {
            select: ({ data, total }) => {
                let processedData = data.map((item: IAtcCode) => {
                    return {
                        id: item.id,
                        name: `${item.code} - ${item.name}`,
                    };
                });

                return {
                    data: processedData,
                    total: total,
                };
            },
        },

        pagination: {
            mode: 'server',
        },
    });

    const { selectProps: efficiencyProofProps } = useSelect<ICategory>({
        resource: 'dictionary/efficiency-proofs',
        defaultValue: [],
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        meta: {},

        pagination: {
            mode: 'server',
        },
    });

    const { selectProps: medicationGroupProps } = useSelect<ICategory>({
        resource: 'dictionary/medication-groups',
        defaultValue: [],
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        meta: {},

        pagination: {
            mode: 'server',
        },
    });

    const { selectProps: countriesProps } = useSelect<ICategory>({
        resource: 'dictionary/countries',
        defaultValue: [],
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        meta: {},

        pagination: {
            mode: 'server',
        },
    });

    const onValuesChange = () => {
        if (!form || typeof form.getFieldsError !== 'function') return;

        const errorList = form?.getFieldsError?.() || [];

        if (!Array.isArray(errorList)) return;

        const clearedFields = errorList
            .filter(error => error.errors.length > 0)
            .map(error => ({
                name: error.name,
                errors: [],
            }));

        if (clearedFields.length > 0) {
            form?.setFields(clearedFields);
        }
    };

    const formPropsForCreate: FormProps = {
        ...formProps,
        onValuesChange: onValuesChange,
        layout: 'vertical',
        initialValues: {
            compositions: [
                {
                    number: undefined,
                    activeIngredients: [
                        {
                            name: undefined,
                            internationalNameId: undefined,
                            quantity: undefined,
                            quantity_unit_id: undefined,
                        },
                    ],
                },
            ],
            packages: [
                {
                    id: undefined,
                    typeId: undefined,
                    description: undefined,
                    isFinal: false,
                    certificateText: undefined,
                },
            ],
            applications: [
                {
                    mohApplicationNumber: undefined,
                    mohApplicationDate: null,
                    typeId: undefined,
                    procedureNotes: undefined,
                },
            ],
        },
    };

    const formId = 'application-create-form';

    const { variant: _saveVariant, ...saveButtonRest } =
        (saveButtonProps as SaveButtonProps & { variant?: never }) ?? {};

    const saveActionProps: Omit<SaveButtonProps, 'variant'> = {
        ...saveButtonRest,
    };

    const headerSaveButtonProps: Omit<SaveButtonProps, 'variant'> = {
        ...saveActionProps,
        form: formId,
        type: 'default',
    };

    return (
        <InternalLayout
            breadcrumbs={<TranslatedBreadcrumbs />}
            onBack={() => replace('/applications')}
            title={t('applications.titles.create', undefined)}
            contentTitle={t(
                'preliminary-applications.titles.application_information',
                undefined,
                'Application information'
            )}
            contentClassName={styles.internalLayout}
            extra={
                <Button
                    {...headerSaveButtonProps}
                    form={formId}
                    icon={<SaveOutlined />}
                    variant="secondary"
                >
                    {t('buttons.save')}
                </Button>
            }
        >
            <Form
                {...formPropsForCreate}
                id={formId}
                className={styles.formContainer}
            >
                <Row gutter={[16, 20]}>
                    <Col xs={24} md={24}>
                        <FormItem name="name">
                            <Input label={t('applications.fields.name_drug')} />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="previous_name">
                            <Input
                                label={t('applications.fields.previous_name')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="name_origin">
                            <Input
                                label={t('applications.fields.name_origin')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="type_id">
                            <Select
                                label={t('applications.fields.procedure')}
                                {...categoryProps}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="pharmaceutical_form">
                            <Select
                                label={t(
                                    'applications.fields.pharmaceutical_form'
                                )}
                                {...pharmaceuticalProps}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="release_form">
                            <Input
                                label={t(
                                    'applications.fields.release_form_details'
                                )}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={12} md={24}>
                        <FormItem name="mibp" valuePropName="checked" noStyle>
                            <Checkbox
                                title={t(
                                    'applications.fields.medical_immunobiological_preparations'
                                )}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={12} md={24}>
                        <FormItem name="is_domestic" valuePropName="checked">
                            <Checkbox
                                title={t('applications.fields.domestic')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="group_id">
                            <Select
                                label={t('applications.fields.group')}
                                {...medicationGroupProps}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="doses_of_medication">
                            <Input
                                label={t(
                                    'applications.fields.doses_of_medication'
                                )}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="primary_package">
                            <Input
                                label={t('applications.fields.primary_package')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="external_package">
                            <Input
                                label={t(
                                    'applications.fields.external_package'
                                )}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="atc_code">
                            <Select
                                {...atcCodeProps}
                                label={t('applications.fields.atc_code')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="additional_atc_code">
                            <Select
                                {...additionalAtcCodeProps}
                                mode="multiple"
                                label={t(
                                    'applications.fields.additional_atc_code'
                                )}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="usage">
                            <Input label={t('applications.fields.usage')} />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="storage_period">
                            <Input
                                label={t('applications.fields.storage_period')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="special_notes">
                            <Input label={t('applications.fields.comment')} />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="efficiency_proof_id">
                            <Select
                                {...efficiencyProofProps}
                                label={t(
                                    'applications.fields.efficiency_proof'
                                )}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="storage_conditions">
                            <Input
                                label={t(
                                    'applications.fields.storage_conditions'
                                )}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="registred_in_countries_ids">
                            <Select
                                {...countriesProps}
                                mode="multiple"
                                label={t(
                                    'applications.fields.registred_in_countries'
                                )}
                            />
                        </FormItem>
                    </Col>
                </Row>

                <CertificatesInCountries />
                <Compositions />
                <Packages />
                <Applications />
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={24}>
                        <FormItem
                            name="numbers_previous_registration_certificates"
                            style={{ marginBottom: 48 }}
                        >
                            <TaggedInput
                                label={t(
                                    'applications.fields.numbers_previous_registration_certificates'
                                )}
                                onChangeInput={tags =>
                                    form?.setFieldsValue({
                                        numbers_previous_registration_certificates:
                                            tags,
                                    })
                                }
                            />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={24}>
                        <FormItem name="applicant">
                            <Select
                                {...applicantProps}
                                label={t('applications.fields.applicant')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="representative_id">
                            <Select
                                {...representativeProps}
                                label={t('applications.fields.representative')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="manufacturers">
                            <Select
                                {...manufacturersProps}
                                mode="multiple"
                                label={t('applications.fields.manufacturers')}
                            />
                        </FormItem>
                    </Col>
                </Row>

                <div className={styles.footerActions}>
                    <Button {...saveActionProps}>{t('buttons.save')}</Button>
                </div>
            </Form>
        </InternalLayout>
    );
};
